import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  afterEveryRender,
  signal,
} from '@angular/core';
import { DataScope } from '../interfaces/dashboard.component';

@Component({
  selector: 'app-scroll-banner',
  standalone: false,
  templateUrl: './scroll-banner.html',
  styleUrl: './scroll-banner.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollBannerComponent {
  @Input() icon = '';
  @Input() heading = '';
  @Input() iconBg = 'var(--orange-bg)';
  @Input() iconColor = 'var(--orange)';
  @Input() loading = false;
  @Input() scope: DataScope | null = null;

  @ViewChild('listEl') private listEl!: ElementRef<HTMLElement>;
  readonly hasOverflow = signal(false);
  // Reading direction of the list (follows the interface language). Drives which
  // way the chevrons point and which way the scroll buttons move the strip.
  readonly isRtl = signal(false);
  // Whether each physical edge currently hides scrolled-away content — drives a
  // soft fade mask on that side so chips melt out instead of hard-cutting.
  readonly fadeLeft = signal(false);
  readonly fadeRight = signal(false);
  // Width of the fade ramp applied at an edge that has hidden content.
  readonly fadeSize = '28px';

  constructor() {
    afterEveryRender(() => this.checkOverflow());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkOverflow();
  }

  onScroll(): void {
    this.updateFades();
  }

  private checkOverflow(): void {
    if (this.listEl?.nativeElement) {
      const el = this.listEl.nativeElement;
      const has = el.scrollWidth > el.clientWidth;
      if (has !== this.hasOverflow()) this.hasOverflow.set(has);
      const rtl = getComputedStyle(el).direction === 'rtl';
      if (rtl !== this.isRtl()) this.isRtl.set(rtl);
      this.updateFades();
    }
  }

  // Compute the distance scrolled from each physical edge. In RTL, browsers
  // report scrollLeft as ≤ 0, so we mirror it — the result is direction-agnostic.
  private updateFades(): void {
    const el = this.listEl?.nativeElement;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const s = el.scrollLeft;
    const fromLeft = this.isRtl() ? max + s : s;
    const fromRight = this.isRtl() ? -s : max - s;
    const left = fromLeft > 1;
    const right = fromRight > 1;
    if (left !== this.fadeLeft()) this.fadeLeft.set(left);
    if (right !== this.fadeRight()) this.fadeRight.set(right);
  }

  // Scroll toward the start (older/earlier items) or end of the list. The
  // physical direction of a positive scrollBy stays the same in both LTR and
  // RTL, so we flip the sign for RTL to keep "start" meaning the reading start.
  scroll(edge: 'start' | 'end'): void {
    const sign = (edge === 'start' ? -1 : 1) * (this.isRtl() ? -1 : 1);
    this.listEl?.nativeElement?.scrollBy({ left: sign * 280, behavior: 'smooth' });
  }
}
